import { StandaloneModal } from '../../../generic/modals/standalone-modal';
import { ModalExampleBox, ModalParagraph, ModalSection, ModalSectionTitle } from '../../../generic/modals/styles';

interface Props {
  isModalOpen: boolean;
  closeModal: () => void;
}

export const ValueChainsModal = ({ isModalOpen, closeModal }: Props) => (
  <StandaloneModal
    title={'What are the value chain parts?'}
    isOpen={isModalOpen}
    modalName="Value chain parts"
    closeModal={closeModal}
  >
    <ModalParagraph>
      When looking at all the ways in which a company impacts the world in and around it, our model considers impacts in
      three parts of the value chain:
    </ModalParagraph>
    <ModalSection>
      <ModalSectionTitle>Internal impacts</ModalSectionTitle>
      <ModalParagraph>
        Internal impacts are caused by activities that happen <b>inside the company</b>.
      </ModalParagraph>
      <ModalExampleBox>
        For example, if a company runs a factory, its internal impacts could include materials and electricity used in
        the factory, emissions produced by the factory, jobs of people it employs and taxes it pays.
      </ModalExampleBox>
      <ModalExampleBox>
        For an IT consulting company, internal impacts could include using the human capital of highly-educated people,
        running its offices and perhaps flying around to meet clients.
      </ModalExampleBox>
    </ModalSection>
    <ModalSection>
      <ModalSectionTitle>Upstream impacts</ModalSectionTitle>
      <ModalParagraph>
        Upstream Impacts come from the <b>suppliers of the company</b>.
      </ModalParagraph>
      <ModalExampleBox>
        For example, for a mobile phone manufacturer these could include impacts caused in all steps of its supply chain
        all the way to, for example, a mine where minerals needed to produce a mobile phone component are dug up from
        the ground.
      </ModalExampleBox>
    </ModalSection>
    <ModalSection>
      <ModalSectionTitle>Downstream impacts</ModalSectionTitle>
      <ModalParagraph>
        Downstream impacts occur when another company, or an end user, actually{' '}
        <b>uses the company's product or service</b>. Downstream impacts are often under-emphasized and poorly
        understood in current impact metrics, but typically largest in terms of absolute size.
      </ModalParagraph>
      <ModalExampleBox>
        For example, for a tobacco company downstream impacts would include the health impacts of smoking in people who
        use their products. For a child day-care center, the downstream impacts might include increasing equality via
        making it possible for mothers to work, as well as increasing contagious viruses among children, as kids kiss
        each other in kindergartens.
      </ModalExampleBox>
      <ModalExampleBox>
        For an IT consulting company downstream impacts are a bit less straightforward to understand. They are
        determined by who the company's customers are, i.e. which companies the IT company helps, and what it helps them
        with. This would imply that if an IT consultancy helps a net positive company become more efficient, that action
        has a positive impact. However, if it makes a net negative operation more efficient, the IT company inherits
        some of that negative impact that it is helping to expand.
      </ModalExampleBox>
    </ModalSection>
    <ModalSection>
      <ModalSectionTitle>Any</ModalSectionTitle>
      <ModalParagraph>
        This category means that the impact may occur in any of the three value chain parts.
      </ModalParagraph>
    </ModalSection>
  </StandaloneModal>
);
